import React from 'react'
import PropTypes from 'prop-types'
import Link from 'gatsby-link'
import withStyles from '@material-ui/core/styles/withStyles'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import sum from 'lodash/sum'
import divide from 'lodash/divide'
import toNumber from 'lodash/toNumber'
import round from 'lodash/round'
import ShareBar from 'components/ShareBar'
import { ArticleMeta } from 'components/Meta'
import Summary from 'components/Reviewpage/Summary'
import { pageStyle } from 'config/theme'
import formatDate from 'utils/formatDate'

const styles = (theme) => ({
  content: {
    ...pageStyle(theme),
    margin: 0,
    padding: 10,
    width: 700,
    [theme.breakpoints.down('sm')]: {
      width: '90%',
    },
  },
  title: {
    textTransform: 'none',
    textAlign: 'center',
    fontWeight: 600,
    color: theme.palette.text.main,
    padding: '8px 0',
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.5em',
    },
  },
  titleSlogan: {
    textAlign: 'center',
    color: theme.palette.text.light,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9em',
    },
  },
  formatDate: {
    color: theme.palette.text.light,
    marginBottom: 25,
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9em',
    },
  },
  authorWrapper: {
    padding: 15,
    color: theme.palette.text.soft,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1em',
    },
  },
  author: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    '&:hover': {
      opacity: 1,
    },
  },
})

const ReviewTemplate = ({ classes, theme, data, pathContext }) => {
  const { reviewBody } = pathContext
  const { markdownRemark: review, authorNode, file } = data
  const baseUrl = 'https://healthfulinspired.com'

  // const datePublished = new Date(review.frontmatter.date).toISOString()
  const datePublished = new Date().toISOString()
  const dateModified = file.modifiedTime

  let authorComponent = null

  if (authorNode) {
    authorComponent = (
      <Link to={authorNode.fields.slug} className={classes.author}>
        {review.frontmatter.originalAuthor}
      </Link>
    )
  } else {
    authorComponent = <span>{review.frontmatter.originalAuthor}</span>
  }

  const statistics = {
    effectiveness: toNumber(review.frontmatter.effectivenessRating),
    valueForMoney: toNumber(review.frontmatter.valueForMoneyRating),
    testimonials: toNumber(review.frontmatter.testimonialsRating),
    company: toNumber(review.frontmatter.companyRating),
  }

  const summaryRating = round(
    divide(
      sum(
        Object.keys(statistics).map((key) => {
          return statistics[key]
        }),
      ),
      4,
    ),
  )

  return (
    <Grid
      id="reviewPage"
      spacing={24}
      justify="center"
      alignItems="center"
      style={{ padding: '20px 0' }}
      container
    >
      <ArticleMeta
        title={review.frontmatter.uiTitle}
        shortDescription={review.frontmatter.shortDescription}
        thumbnail={review.frontmatter.thumbnail}
        genre="health"
        tags={review.frontmatter.tags}
        wordCount={review.wordCount.words}
        datePublished={datePublished}
        dateModified={dateModified}
        articleBody={reviewBody}
        excerpt={review.excerpt}
        path={review.fields.slug}
      />
      <Grid className={classes.content} item>
        <Typography variant="display1" className={classes.title}>
          {review.frontmatter.uiTitle}
        </Typography>
        {review.frontmatter.uiTitleSlogan && (
          <Typography variant="subheading" className={classes.titleSlogan}>
            {review.frontmatter.uiTitleSlogan}
          </Typography>
        )}
        <Typography align="center" className={classes.formatDate}>
          {formatDate(new Date())} {'\u2022'} {review.timeToRead} min read
        </Typography>
        <Summary
          header={`${review.frontmatter.productName} by ${
            review.frontmatter.productCreator
          }`}
          value={summaryRating}
          statistics={statistics}
          pros={review.frontmatter.pros}
          cons={review.frontmatter.cons}
          to={review.frontmatter.landingUrl}
        />
        <ShareBar
          baseUrl={baseUrl}
          url={`${baseUrl}${review.fields.slug}`}
          thumbnail={review.frontmatter.thumbnail}
          title={review.frontmatter.title}
          excerpt={review.excerpt}
          tags={review.frontmatter.tags}
        />
        <Typography align="center" className={classes.authorWrapper}>
          Reviewed by: {authorComponent}
        </Typography>
        <div
          className="markdown"
          dangerouslySetInnerHTML={{ __html: review.html }}
        />
      </Grid>
      <style>{`
        .markdown {
          color: ${theme.palette.text.main};
        }
        h1, h2, h3, h4, h5, h6 {
          line-height: 1em;
        }
        h1 {
          padding: 20px 0;
        }
        @media (max-width: 600px) {
          .markdown h1, h2, h3 {
            font-size: 1.4em;
          }
        }
      `}</style>
    </Grid>
  )
}

ReviewTemplate.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  pathContext: PropTypes.shape({
    reviewBody: PropTypes.string.isRequired,
  }).isRequired,
}

// eslint-disable-next-line
export const query = graphql`
  query ReviewQuery(
    $path: String!
    $fileAbsolutePath: String!
    $originalAuthor: String
  ) {
    markdownRemark(fields: { slug: { eq: $path } }) {
      html
      excerpt(pruneLength: 300)
      timeToRead
      wordCount {
        words
      }
      fields {
        slug
      }
      frontmatter {
        title
        uiTitle
        uiTitleSlogan
        productName
        productCreator
        originalAuthor
        landingUrl
        date
        shortDescription
        effectivenessRating
        valueForMoneyRating
        testimonialsRating
        companyRating
        pros
        cons
        tags
      }
    }
    file(absolutePath: { eq: $fileAbsolutePath }) {
      modifiedTime
    }
    authorNode: markdownRemark(
      frontmatter: {
        pageType: { eq: "author" }
        title: { eq: $originalAuthor }
      }
    ) {
      fields {
        slug
      }
    }
  }
`

export default withStyles(styles, { withTheme: true })(ReviewTemplate)



// WEBPACK FOOTER //
// ./src/templates/review.js